import React, { useEffect } from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import PanicError from 'Errors/Panic';

async function submitResource(dispatch, state, resource, args = {}) {
  const {
    actionName = 'setLastResource',
  } = args;

  try {
    // TODO: Add hook to set resources before the resource submission has completed.
    const response = await resource.submit();
    const [submittedResource] = response;

    dispatch({ type: actionName, resource: submittedResource });

    return response;
  } catch (err) {
    dispatch({ type: 'setSubmissionError', loadingError: err }); // TODO: this does not exist in the PCC.

    throw err;
  }
}

async function deleteResource(dispatch, state, resource) {
  try {
    // TODO: Add hook to set resources before the resource submission has completed.
    await resource.delete({ raw: true });
    dispatch({ type: 'deleteResource', resource });
  } catch (err) {
    dispatch({ type: 'setSubmissionError', loadingError: err }); // TODO: this does not exist in the PCC.

    throw err;
  }
}

function newResource(state) {
  // Create New Resource
  return state.collection?.create();
}

function reversedResources(state) {
  const { resources } = state;

  const reverseResources = [];
  for (let i = 0; i < resources.length; i++) {
    reverseResources.unshift(resources[i]);
  }
  return reverseResources;
}

async function fetchData(link, args) {
  const {
    onFetchStart,
    onFetchSuccess,
    onFetchError,
    onFetchFinally,
  } = args;

  let resp;
  try {
    onFetchStart();

    resp = await fr.http.get(link);

    onFetchSuccess(resp);
  } catch (err) {
    onFetchError(err);
  }

  onFetchFinally(resp);
}

function loadPreviousResourceSet(dispatch, state) {
  return fetchData(state.prevLink, {
    onFetchStart: () => {
      dispatch({ type: 'setLoading', loading: true });
    },
    onFetchSuccess: (resp) => {
      dispatch({
        type: 'setPrevPage',
        prevLink: (resp.link('prev')) ? resp.link('prev').url() : null,
        resources: resp.resources().list(),
        originalSelfLink: state.selfLink,
      });
    },
    onFetchError: (err) => {
      dispatch({ type: 'setLoadingError', loadingError: err });
    },
    onFetchFinally: () => {
      dispatch({ type: 'setLoading', loading: false });
    },
  });
}

function loadNextResourceSet(dispatch, state) {
  return fetchData(state.nextLink, {
    onFetchStart: () => {
      dispatch({ type: 'setLoading', loading: true });
    },
    onFetchSuccess: (resp) => {
      dispatch({
        type: 'setNextPage',
        nextLink: (resp.link('next')) ? resp.link('next').url() : null,
        resources: resp.resources().list(),
        originalSelfLink: state.selfLink,
      });
    },
    onFetchError: (err) => {
      dispatch({ type: 'setLoadingError', loadingError: err });
    },
    onFetchFinally: () => {
      dispatch({ type: 'setLoading', loading: false });
    },
  });
}

async function initializeCollection(dispatch, linkUrl) {
  fetchData(linkUrl, {
    onFetchStart: () => {
      dispatch({ type: 'setLoading', loading: true });
    },
    onFetchSuccess: (resp) => {
      dispatch({ type: 'setCollection', collection: resp });
    },
    onFetchError: (err) => {
      dispatch({ type: 'setLoadingError', loadingError: err });
    },
    onFetchFinally: () => {
      dispatch({ type: 'setLoading', loading: false });
    },
  });
}

// initial state is a required prop
function init(initialState) {
  const {
    collectionSelfLink,
    collection,
  } = initialState;

  // If a collection is provided then just set its values.
  if (collection) {
    const prevLink = collection.link('prev')?.url();
    const nextLink = collection.link('next')?.url();

    return {
      loading: false,
      loadingError: null,
      selfLink: collection.link('self').url(),
      collection,
      resources: collection.resources().list(),
      nextLink,
      prevLink,
      hasNextLink: !!nextLink,
      hasPrevLink: !!prevLink,
    };
  }

  // If only a self link is provided, then go into a loading state.
  if (collectionSelfLink) {
    return {
      loading: true,
      loadingError: null,
      selfLink: collectionSelfLink,
      collection: null,
      resources: [],
      nextLink: null,
      prevLink: null,
      hasNextLink: false,
      hasPrevLink: false,
    };
  }

  // Otherwise throw an error because this context is not setup correctly. Should be guaranteed either a collection or self link.
  throw new PanicError('invalid-initial-state', 'One of collection or collectionSelfLink are required.');
}


const isMatching = (item1, item2) => {
  return item1?.id() === item2?.id() && item1?.type() === item2?.type();
};

const notMatching = (item1, item2) => {
  return !isMatching(item1, item2);
};

const replaceMatching = (item1, item2) => {
  if (isMatching(item1, item2)) {
    return item2;
  }

  return item1;
};

function reducer(state, action) {
  switch (action.type) {
    case 'setLoading': {
      return {
        ...state,
        loading: action.loading,
      };
    }
    case 'setLoadingError': {
      return {
        ...state,
        loadingError: action.loadingError,
      };
    }
    case 'setSubmissionError': {
      return {
        ...state,
        submissionError: action.submissionError,
      };
    }
    case 'setFirstResource': {
      return {
        ...state,
        resources: [action.resource, ...state.resources],
      };
    }
    case 'setLastResource': {
      return {
        ...state,
        resources: [...state.resources, action.resource],
      };
    }
    case 'deleteResource': {
      return {
        ...state,
        resources: state.resources.filter(el => notMatching(el, action.resource)),
      };
    }
    case 'updateResource': {
      const updatedResources = state.resources.map(el => replaceMatching(el, action.resource));

      return {
        ...state,
        resources: updatedResources,
      };
    }
    case 'setCollection': {
      if (!action.collection?.link('self')) {
        return {
          ...state,
        };
      }
      return {
        ...state,
        collection: action.collection,
        selfLink: action.collection?.link('self').url(),
        resources: action.collection?.resources().list(),
        nextLink: action.collection?.link('next')?.url(),
        hasNextLink: !!action.collection?.link('next')?.url(),
        prevLink: action.collection?.link('prev')?.url(),
        hasPrevLink: !!action.collection?.link('prev')?.url(),
      };
    }
    case 'setResources': {
      return {
        ...state,
        resources: action.resources,
      };
    }
    case 'setNextPage': {
      if (state.selfLink !== action.originalSelfLink) {
        // Stale check
        return state;
      }

      return {
        ...state,
        nextLink: action.nextLink,
        hasNextLink: !!action.nextLink,
        resources: [...state.resources, ...action.resources], // Set new resources after existing ones
      };
    }
    case 'setPrevPage': {
      if (state.selfLink !== action.originalSelfLink) {
        // Stale check
        return state;
      }

      return {
        ...state,
        prevLink: action.prevLink,
        hasPrevLink: !!action.nextLink,
        resources: [...action.resources, ...state.resources], // Set new resources before existing ones
      };
    }
    case 'setSelfLink': {
      return {
        ...state,
        selfLink: action.selfLink,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function PagedCollectionProvider({
  StateContext,
  DispatchContext,
  children,
  initialState,
}) {
  const [state, dispatch] = React.useReducer(reducer, initialState, init);
  useEffect(() => {
    if (state.collection) {
      initializeCollection(dispatch, initialState.collectionSelfLink);
    }
  }, [initialState.collectionSelfLink]);

  useEffect(() => {
    if (!state.collection) {
      initializeCollection(dispatch, state.selfLink);
    }
  }, [state.selfLink]);

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </StateContext.Provider>
  );
}

function usePagedCollectionState(stateContext) {
  const context = React.useContext(stateContext);

  if (context === undefined) {
    throw new Error('usePagedCollectionState must be used within a PagedCollectionProvider');
  }

  return context;
}

function usePagedCollectionDispatch(dispatchContext) {
  const context = React.useContext(dispatchContext);

  if (context === undefined) {
    throw new Error('usePagedCollectionDispatch must be used within a PagedCollectionProvider');
  }

  return context;
}

PagedCollectionProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  initialState: PropTypes.object.isRequired,
  StateContext: PropTypes.object.isRequired,
  DispatchContext: PropTypes.object.isRequired,
};

export {
  PagedCollectionProvider,
  usePagedCollectionState,
  usePagedCollectionDispatch,
  initializeCollection,
  loadNextResourceSet,
  loadPreviousResourceSet,
  newResource,
  reversedResources,
  submitResource,
  deleteResource,
  fetchData,
};
